import { colors } from '@sweep-io/sweep-design';

import {
  ControlLinePosition,
  ResizeControlVariant,
  ResizeParams,
  ResizeDragEvent,
  XY_RESIZER_HANDLE_POSITIONS,
  ResizeParamsWithDirection,
} from '@xyflow/system';
import { useSelectiveAspectRatioResizerCtx } from './SelectiveAspectRatioResizerCtx';
import { useCallback } from 'react';
import { calculateIsResizingHorizontal } from './helper';

import { NodeResizeControl, NodeResizerProps, useViewport } from '@xyflow/react';

const styles = {
  handle: {
    width: 12,
    height: 12,
    zIndex: 1000,
  },
  lineStyle: {
    borderColor: colors.blue[500],
    borderWidth: 1,
  },
} as const;

const Y_RESIZER_LINE_POSITIONS: ControlLinePosition[] = ['top', 'bottom'];
const X_RESIZER_LINE_POSITIONS: ControlLinePosition[] = ['left', 'right'];

const getResizingPositionFromEvent = (event: ResizeDragEvent) => {
  const classes = event.sourceEvent.target.className.split(' ');

  if (classes.includes('line')) {
    if (classes.includes('right')) {
      return 'right';
    }
    if (classes.includes('left')) {
      return 'left';
    }
  }
  if (classes.includes('handle')) {
    if (classes.includes('top') && classes.includes('right')) {
      return 'top-right';
    }
    if (classes.includes('top') && classes.includes('left')) {
      return 'top-left';
    }
    if (classes.includes('bottom') && classes.includes('right')) {
      return 'bottom-right';
    }
    if (classes.includes('bottom') && classes.includes('left')) {
      return 'bottom-left';
    }
  }
  return null;
};

export const SelectiveAspectRatioResizer = ({
  nodeId,
  isVisible = true,
  handleClassName,
  handleStyle,
  lineClassName,
  lineStyle,
  color,
  minWidth = 10,
  minHeight = 10,
  maxWidth = Number.MAX_VALUE,
  maxHeight = Number.MAX_VALUE,
  defaultKeepAspectRatio = false,
  horizontalKeepAspectRatio = false,
  shouldResize,
  onResizeStart,
  onResize,
  onResizeEnd,
  allowedWidths,
}: Omit<NodeResizerProps, 'keepAspectRatio' | 'onResizeEnd'> & {
  horizontalKeepAspectRatio?: boolean;
  defaultKeepAspectRatio?: boolean;
  allowedWidths?: number[];
  onResizeEnd?: (
    event: ResizeDragEvent,
    params: ResizeParams,
    initialWidth: number | undefined,
    isResizingHorizontal: boolean,
  ) => void;
}) => {
  const { zoom } = useViewport();
  const { setResizingPosition, initialWidthRef } = useSelectiveAspectRatioResizerCtx();
  const _onResizeStart = useCallback(
    (event: ResizeDragEvent, params: ResizeParams) => {
      initialWidthRef.current = params.width || undefined;

      setResizingPosition(getResizingPositionFromEvent(event));
      onResizeStart?.(event, params);
    },
    [initialWidthRef, onResizeStart, setResizingPosition],
  );

  const _onResize = useCallback(
    (event: ResizeDragEvent, params: ResizeParamsWithDirection) => {
      onResize?.(event, params);
    },
    [onResize],
  );

  const _onResizeEnd = useCallback(
    (event: ResizeDragEvent, params: ResizeParams) => {
      const initialWidth = initialWidthRef.current;
      const isResizingHorizontal = calculateIsResizingHorizontal(
        getResizingPositionFromEvent(event),
      );
      setResizingPosition(null);
      onResizeEnd?.(event, params, initialWidth, isResizingHorizontal);
    },
    [initialWidthRef, onResizeEnd, setResizingPosition],
  );

  if (!isVisible) return null;

  const inverseZoom = 1 / zoom;

  return (
    <>
      {Y_RESIZER_LINE_POSITIONS.map((position) => (
        <NodeResizeControl
          key={position}
          className={lineClassName}
          style={{ ...styles.lineStyle, ...lineStyle, scale: inverseZoom }}
          nodeId={nodeId}
          position={position}
          variant={ResizeControlVariant.Line}
          color={color}
          minWidth={minWidth}
          minHeight={minHeight}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          onResizeStart={_onResizeStart}
          keepAspectRatio={defaultKeepAspectRatio}
          shouldResize={shouldResize}
          onResize={_onResize}
          onResizeEnd={_onResizeEnd}
        />
      ))}
      {X_RESIZER_LINE_POSITIONS.map((position) => (
        <NodeResizeControl
          key={position}
          className={lineClassName}
          style={{ ...styles.lineStyle, ...lineStyle, scale: inverseZoom }}
          nodeId={nodeId}
          position={position}
          variant={ResizeControlVariant.Line}
          color={color}
          minWidth={allowedWidths ? Math.min(...allowedWidths) : minWidth}
          minHeight={minHeight}
          maxWidth={allowedWidths ? Math.max(...allowedWidths) : maxWidth}
          maxHeight={maxHeight}
          onResizeStart={_onResizeStart}
          keepAspectRatio={horizontalKeepAspectRatio}
          shouldResize={shouldResize}
          onResize={_onResize}
          onResizeEnd={_onResizeEnd}
        />
      ))}
      {XY_RESIZER_HANDLE_POSITIONS.map((position) => (
        <NodeResizeControl
          key={position}
          className={handleClassName}
          style={{ ...styles.handle, ...handleStyle, scale: inverseZoom }}
          nodeId={nodeId}
          position={position}
          color={color}
          minWidth={minWidth}
          minHeight={minHeight}
          maxWidth={maxWidth}
          maxHeight={maxHeight}
          onResizeStart={_onResizeStart}
          keepAspectRatio={defaultKeepAspectRatio}
          shouldResize={shouldResize}
          onResize={_onResize}
          onResizeEnd={_onResizeEnd}
        />
      ))}
    </>
  );
};
